import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-labelledby", "aria-describedby"]
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = {
  key: 0,
  class: "modal__information"
}
const _hoisted_4 = ["innerHTML"]

import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { ModalVariant } from "./modal-variants";
import {getTranslations} from "@services/translations";


export default /*@__PURE__*/_defineComponent({
  __name: 'Modal',
  props: {
    show: { type: Boolean },
    bottom: { type: Boolean },
    heading: {},
    content: {},
    closeOnBackdrop: { type: Boolean },
    variant: {}
  },
  emits: ["update:show", "closeModal"],
  setup(__props: any, { emit: __emit }) {

const translations = getTranslations();

const props = __props;

const emit = __emit;

const isOpen = ref(props.show === true);
const modalVariant = computed(() => props.variant ?? ModalVariant.Default);
const modalElement = ref<HTMLElement | null>(null);
const closeButtonElement = ref<HTMLButtonElement | null>(null);
const lastFocusedElement = ref<HTMLElement | null>(null);
const modalId = `modal-${getCurrentInstance()?.uid ?? 0}`;
const headingId = `${modalId}-heading`;
const contentId = `${modalId}-content`;

watch(
  () => props.show,
  (newValue) => {
    const shouldOpen = newValue === true;
    if (shouldOpen !== isOpen.value) {
      setOpen(shouldOpen);
    }
  },
);

watch(isOpen, (newValue) => {
  emit("update:show", newValue);
  toggleBodyClass(newValue);

  if (newValue) {
    window.addEventListener("resize", updateScrollableState);
    nextTick(() => {
      updateScrollableState();
      focusModal();
    });
    return;
  }

  window.removeEventListener("resize", updateScrollableState);
  restoreFocus();
});

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
  if (isOpen.value) {
    toggleBodyClass(true);
    nextTick(() => {
      updateScrollableState();
      focusModal();
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("resize", updateScrollableState);
  toggleBodyClass(false);
});

function setOpen(value: boolean) {
  if (value && !isOpen.value) {
    lastFocusedElement.value = document.activeElement as HTMLElement | null;
  }

  isOpen.value = value;
}

function openModal() {
  setOpen(true);
}

function closeModal() {
  setOpen(false);
  emit("closeModal");
}

function toggleModal() {
  setOpen(!isOpen.value);
}

function onBackdropClick() {
  if (props.closeOnBackdrop !== false) {
    closeModal();
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape" && isOpen.value) {
    closeModal();
  }
}

function updateScrollableState() {
  if (!modalElement.value) {
    return;
  }

  const modal = modalElement.value;
  const shouldScroll = modal.scrollHeight > window.innerHeight;
  modal.classList.toggle("modal--scroll", shouldScroll);
}

function focusModal() {
  const focusTarget = closeButtonElement.value ?? modalElement.value;
  focusTarget?.focus();
}

function restoreFocus() {
  lastFocusedElement.value?.focus();
  lastFocusedElement.value = null;
}

function toggleBodyClass(forceState: boolean) {
  const body = document.body;
  if (forceState) {
    body.classList.add("modal-open");
    return;
  }

  nextTick(() => {
    const hasOpenModals = document.querySelectorAll(".modal-wrapper").length > 0;
    body.classList.toggle("modal-open", hasOpenModals);
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _renderSlot(_ctx.$slots, "link", {
      clicked: toggleModal,
      open: openModal,
      close: closeModal
    }),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "modal-wrapper",
            onClick: _withModifiers(onBackdropClick, ["self"])
          }, [
            _createElementVNode("div", {
              ref_key: "modalElement",
              ref: modalElement,
              class: _normalizeClass(["modal", { 'modal--wide': modalVariant.value === _unref(ModalVariant).Wide, 'modal--bottom': __props.bottom }]),
              role: "dialog",
              "aria-modal": "true",
              "aria-labelledby": __props.heading ? headingId : undefined,
              "aria-describedby": __props.content ? contentId : undefined,
              tabindex: "-1"
            }, [
              _createElementVNode("button", {
                ref_key: "closeButtonElement",
                ref: closeButtonElement,
                class: "modal__close-button",
                type: "button",
                "aria-label": _unref(translations)['bookings.common.close'],
                onClick: closeModal
              }, [...(_cache[0] || (_cache[0] = [
                _createElementVNode("span", {
                  class: "icon icon--cross",
                  "aria-hidden": "true"
                }, null, -1 /* CACHED */)
              ]))], 8 /* PROPS */, _hoisted_2),
              (__props.heading || __props.content)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    (__props.heading)
                      ? (_openBlock(), _createElementBlock("h1", {
                          key: 0,
                          id: headingId
                        }, _toDisplayString(__props.heading), 1 /* TEXT */))
                      : _createCommentVNode("v-if", true),
                    (__props.content)
                      ? (_openBlock(), _createElementBlock("p", {
                          key: 1,
                          id: contentId,
                          class: "modal__preamble",
                          innerHTML: __props.content
                        }, null, 8 /* PROPS */, _hoisted_4))
                      : _createCommentVNode("v-if", true)
                  ]))
                : _createCommentVNode("v-if", true),
              _renderSlot(_ctx.$slots, "content", { close: closeModal })
            ], 10 /* CLASS, PROPS */, _hoisted_1)
          ]))
        : _createCommentVNode("v-if", true)
    ]))
  ], 64 /* STABLE_FRAGMENT */))
}
}

})